import { useEffect, useState, useCallback, useRef } from 'react'

import { APP_DEFAULT_STATE } from '@api/local'
import { DeviceTypeEnum, useGetAppQuery } from '@hooks/api'

// Extend the Window interface to include the zE object
declare global {
  interface Window {
    zE?: (command: 'webWidget' | 'webWidget:on' | 'webWidget:off' | 'webWidget:show' | 'webWidget:open' | 'webWidget:hide', ...args: unknown[]) => void
    zESettings?: {
      webWidget?: {
        suppress?: boolean
        onLoad?: () => void
      }
    }
  }
}

const ZENDESK_KEY = '882a776c-4056-4124-a7f7-752be44ad21a'
const ZENDESK_SCRIPT_URL = `https://static.zdassets.com/ekr/snippet.js?key=${ZENDESK_KEY}`
const SCRIPT_LOAD_TIMEOUT = 10000 // 10 seconds

export const useZendesk = () => {
  const { data: appData = { app: { ...APP_DEFAULT_STATE } } } = useGetAppQuery()
  const isMobile = appData?.app?.deviceType === DeviceTypeEnum.MOBILE

  const [isZendeskReady, setIsZendeskReady] = useState(false)
  const listenerAddedRef = useRef(false)
  const isMountedRef = useRef(true)

  const safeZE = useCallback((...args: Parameters<NonNullable<Window['zE']>>) => {
    try {
      if (window.zE) {
        window.zE(...args)
      }
    } catch (error) {
      // Silently handle Zendesk errors
    }
  }, [])

  const addCloseListener = useCallback(() => {
    if (listenerAddedRef.current) return
    safeZE('webWidget:on', 'close', () => {
      if (isMobile && isMountedRef.current) {
        safeZE('webWidget', 'hide')
      }
    })
    listenerAddedRef.current = true
  }, [isMobile, safeZE])

  const setupZendeskSettings = useCallback(() => {
    window.zESettings = {
      webWidget: {
        suppress: isMobile,
        onLoad: () => {
          if (isMountedRef.current) {
            setIsZendeskReady(true)
            addCloseListener()
          }
        },
      },
    }
  }, [isMobile, addCloseListener])

  const handleExistingZE = useCallback(() => {
    if (isMobile) {
      safeZE('webWidget', 'hide')
    }
    addCloseListener()
    if (isMountedRef.current) {
      setIsZendeskReady(true)
    }
  }, [isMobile, safeZE, addCloseListener])

  const handleExistingScript = useCallback(() => {
    const scriptElement = document.getElementById('ze-snippet') as HTMLScriptElement | null
    if (!scriptElement) return false

    scriptElement.onload = () => {
      if (window.zE && isMountedRef.current) {
        if (isMobile) {
          safeZE('webWidget', 'hide')
        }
        addCloseListener()
        setIsZendeskReady(true)
      }
    }
    return true
  }, [isMobile, safeZE, addCloseListener])

  const injectScript = useCallback(() => {
    if (!document.body) return

    const script = document.createElement('script')
    script.id = 'ze-snippet'
    script.src = ZENDESK_SCRIPT_URL
    script.async = true

    const timeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        // Script failed to load within timeout - mark as ready to prevent hanging
        setIsZendeskReady(true)
      }
    }, SCRIPT_LOAD_TIMEOUT)

    script.onload = () => {
      clearTimeout(timeoutId)
      if (window.zE && isMountedRef.current) {
        if (isMobile) {
          safeZE('webWidget', 'hide')
        }
        addCloseListener()
        setIsZendeskReady(true)
      }
    }

    document.body.appendChild(script)
  }, [isMobile, safeZE, addCloseListener])

  useEffect(() => {
    setupZendeskSettings()

    if (window.zE) {
      handleExistingZE()
      return
    }

    if (handleExistingScript()) {
      return
    }

    injectScript()

    return () => {
      isMountedRef.current = false
      if (window.zE && listenerAddedRef.current) {
        safeZE('webWidget:off', 'close')
        listenerAddedRef.current = false
      }
    }
  }, [setupZendeskSettings, handleExistingZE, handleExistingScript, injectScript, safeZE])

  const toggleZendeskWidget = useCallback(() => {
    if (isZendeskReady) {
      safeZE('webWidget', 'show')
      safeZE('webWidget', 'open')
    }
  }, [isZendeskReady, safeZE])

  return { isZendeskReady, toggleZendeskWidget }
}
